<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import zernioClient from 'dashboard/api/channel/zernioClient';
import Button from 'dashboard/components-next/button/Button.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';

const { t } = useI18n();

const SURFACES = [
  {
    key: 'facebook/dm',
    platform: 'facebook',
    surface: 'dm',
    icon: 'i-ri-facebook-circle-fill',
    label: 'Facebook — Messages',
  },
  {
    key: 'facebook/comment',
    platform: 'facebook',
    surface: 'comment',
    icon: 'i-ri-chat-3-line',
    label: 'Facebook — Post comments',
    isPublic: true,
  },
  {
    key: 'instagram/dm',
    platform: 'instagram',
    surface: 'dm',
    icon: 'i-ri-instagram-line',
    label: 'Instagram — Messages',
  },
  {
    key: 'instagram/comment',
    platform: 'instagram',
    surface: 'comment',
    icon: 'i-ri-chat-3-line',
    label: 'Instagram — Post comments',
    isPublic: true,
  },
];

// Each credential set is a separate Zernio account, so every brand keeps its
// own free-tier accounts and its own webhook signing secret.
const credentials = computed(
  () => window.chatwootConfig?.zernioCredentials || ['default']
);
const selectedCredential = ref('default');
const selectedKey = ref('facebook/dm');
const profileId = ref('');
const inboxName = ref('');
// Zernio requires profileId on /connect/{platform}, and it must be a real
// profile id — a name is rejected with "Invalid profile ID format". Load the
// account's profiles so the admin picks rather than guesses.
const profiles = ref([]);
const isLoadingProfiles = ref(false);
const profilesError = ref('');
const isRequestingAuthorization = ref(false);
const hasError = ref(false);
const errorStateMessage = ref('');
const errorStateDescription = ref('');

const selected = computed(() =>
  SURFACES.find(option => option.key === selectedKey.value)
);

// e.g. "FacebookZ - S3", "InstagramZ Comments - S3". Shown as the placeholder
// and sent verbatim when the field is left blank, so the created inbox always
// matches what the form displays.
const suggestedName = computed(() => {
  const { platform, surface } = selected.value;
  const platformLabel = platform.charAt(0).toUpperCase() + platform.slice(1);
  const parts = [
    `${platformLabel}Z${surface === 'comment' ? ' Comments' : ''}`,
  ];
  if (selectedCredential.value !== 'default') {
    parts.push(selectedCredential.value.toUpperCase());
  }
  return parts.join(' - ');
});

// Paste these into Zernio → Settings → Webhooks, one per Zernio account.
// Each credential set gets its own path so a payload signed by one brand's
// secret cannot validate on the other's path.
const webhookEndpoints = computed(() => {
  const host = window.chatwootConfig?.hostURL || window.location.origin;
  return credentials.value.map(credential => ({
    credential,
    url: `${host}/webhooks/zernio/${credential}`,
  }));
});

const onCopy = async url => {
  await copyTextToClipboard(url);
  useAlert(t('INBOX_MGMT.ADD.ZERNIO.WEBHOOK_COPIED'));
};

const loadProfiles = async () => {
  isLoadingProfiles.value = true;
  profilesError.value = '';
  profiles.value = [];
  profileId.value = '';
  try {
    const { data } = await zernioClient.getProfiles(selectedCredential.value);
    profiles.value = data.profiles || [];
    if (profiles.value.length === 1) {
      profileId.value = profiles.value[0].id;
    }
  } catch (error) {
    profilesError.value =
      error?.response?.data?.error ||
      error?.message ||
      'Could not load profiles';
  } finally {
    isLoadingProfiles.value = false;
  }
};

// Profiles are per Zernio account, so reload whenever the brand changes.
watch(selectedCredential, loadProfiles);

const canConnect = computed(
  () => !isRequestingAuthorization.value && profileId.value.trim().length > 0
);

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const errorCode = urlParams.get('code');
  const errorMessage = urlParams.get('error_message');

  if (errorMessage) {
    hasError.value = true;
    if (errorCode === '400') {
      errorStateMessage.value = errorMessage;
      errorStateDescription.value = t('INBOX_MGMT.ADD.ZERNIO.ERROR_AUTH');
    } else {
      errorStateMessage.value = t('INBOX_MGMT.ADD.ZERNIO.ERROR_MESSAGE');
      errorStateDescription.value = errorMessage;
    }
  }
  // Drop the error params so a reload does not resurface a stale failure.
  window.history.replaceState({}, document.title, window.location.pathname);

  if (!hasError.value) loadProfiles();
});

const requestAuthorization = async () => {
  isRequestingAuthorization.value = true;
  try {
    const {
      data: { url },
    } = await zernioClient.generateAuthorization({
      platform: selected.value.platform,
      surface: selected.value.surface,
      profile_id: profileId.value.trim(),
      credential: selectedCredential.value,
      inbox_name: inboxName.value.trim() || suggestedName.value,
    });
    window.location.href = url;
  } catch (error) {
    isRequestingAuthorization.value = false;
    hasError.value = true;
    errorStateMessage.value = t('INBOX_MGMT.ADD.ZERNIO.ERROR_MESSAGE');
    errorStateDescription.value =
      error?.response?.data?.error || error?.message || '';
  }
};
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full text-center">
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
      </div>
      <div
        v-else
        class="flex flex-col items-center justify-center w-full max-w-lg px-8 py-10 text-center rounded-2xl outline outline-1 outline-n-weak"
      >
        <h6 class="text-2xl font-medium">
          {{ $t('INBOX_MGMT.ADD.ZERNIO.TITLE') }}
        </h6>
        <p class="py-4 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.ZERNIO.HELP') }}
        </p>

        <div v-if="credentials.length > 1" class="w-full mb-4 text-left">
          <label class="block mb-2 text-sm font-medium">
            {{ $t('INBOX_MGMT.ADD.ZERNIO.CREDENTIAL_LABEL') }}
          </label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="credential in credentials"
              :key="credential"
              type="button"
              class="px-4 py-2 text-sm rounded-xl outline outline-1"
              :class="
                selectedCredential === credential
                  ? 'outline-n-brand bg-n-alpha-2'
                  : 'outline-n-weak'
              "
              @click="selectedCredential = credential"
            >
              {{ credential }}
            </button>
          </div>
          <p class="mt-1 text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.ADD.ZERNIO.CREDENTIAL_HELP') }}
          </p>
        </div>

        <div class="w-full mb-4 text-left">
          <label class="block mb-2 text-sm font-medium">
            {{ $t('INBOX_MGMT.ADD.ZERNIO.SURFACE_LABEL') }}
          </label>
          <div class="grid grid-cols-1 gap-2">
            <button
              v-for="option in SURFACES"
              :key="option.key"
              type="button"
              class="flex items-center gap-3 px-4 py-3 text-sm text-left rounded-xl outline outline-1"
              :class="
                selectedKey === option.key
                  ? 'outline-n-brand bg-n-alpha-2'
                  : 'outline-n-weak'
              "
              @click="selectedKey = option.key"
            >
              <span :class="option.icon" class="text-lg" />
              <span class="flex-1">{{ option.label }}</span>
              <span
                v-if="option.isPublic"
                class="px-2 py-0.5 text-xs rounded-full bg-n-amber-3 text-n-amber-11"
              >
                {{ $t('INBOX_MGMT.ADD.ZERNIO.PUBLIC_BADGE') }}
              </span>
            </button>
          </div>
        </div>

        <div class="w-full mb-4 text-left">
          <label class="block mb-2 text-sm font-medium" for="zernio-inbox-name">
            {{ $t('INBOX_MGMT.ADD.ZERNIO.INBOX_NAME_LABEL') }}
          </label>
          <input
            id="zernio-inbox-name"
            v-model="inboxName"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-xl outline outline-1 outline-n-weak bg-n-alpha-black2"
            :placeholder="suggestedName"
          />
          <p class="mt-1 text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.ADD.ZERNIO.INBOX_NAME_HELP') }}
          </p>
        </div>

        <div class="w-full mb-6 text-left">
          <label class="block mb-2 text-sm font-medium" for="zernio-profile-id">
            {{ $t('INBOX_MGMT.ADD.ZERNIO.PROFILE_ID_LABEL') }}
          </label>

          <p v-if="isLoadingProfiles" class="text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.ADD.ZERNIO.PROFILE_LOADING') }}
          </p>

          <select
            v-else-if="profiles.length"
            id="zernio-profile-id"
            v-model="profileId"
            class="w-full px-3 py-2 text-sm rounded-xl outline outline-1 outline-n-weak bg-n-alpha-black2"
          >
            <option value="" disabled>
              {{ $t('INBOX_MGMT.ADD.ZERNIO.PROFILE_SELECT') }}
            </option>
            <option
              v-for="profile in profiles"
              :key="profile.id"
              :value="profile.id"
            >
              {{ profile.name }}
            </option>
          </select>

          <!-- Fall back to manual entry if the profile list could not load. -->
          <input
            v-else
            id="zernio-profile-id"
            v-model="profileId"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-xl outline outline-1 outline-n-weak bg-n-alpha-black2"
            :placeholder="$t('INBOX_MGMT.ADD.ZERNIO.PROFILE_ID_PLACEHOLDER')"
          />

          <p
            v-if="profilesError"
            class="mt-1 text-xs text-n-ruby-11"
            data-testid="zernio-profiles-error"
          >
            {{ profilesError }}
          </p>
          <p class="mt-1 text-xs text-n-slate-10">
            {{ $t('INBOX_MGMT.ADD.ZERNIO.PROFILE_ID_HELP') }}
          </p>
        </div>

        <p
          v-if="selected.isPublic"
          class="w-full p-3 mb-4 text-xs text-left rounded-xl bg-n-amber-3 text-n-amber-11"
        >
          {{ $t('INBOX_MGMT.ADD.ZERNIO.PUBLIC_WARNING') }}
        </p>

        <Button
          class="!rounded-full !px-6"
          lg
          icon="i-ri-link"
          :disabled="!canConnect"
          :is-loading="isRequestingAuthorization"
          :label="$t('INBOX_MGMT.ADD.ZERNIO.CONNECT')"
          @click="requestAuthorization()"
        />
      </div>

      <div
        v-if="!hasError"
        class="w-full max-w-lg p-6 mt-6 text-left rounded-2xl outline outline-1 outline-n-weak"
      >
        <h6 class="text-sm font-medium">
          {{ $t('INBOX_MGMT.ADD.ZERNIO.WEBHOOK_TITLE') }}
        </h6>
        <p class="mt-1 mb-4 text-xs text-n-slate-10">
          {{ $t('INBOX_MGMT.ADD.ZERNIO.WEBHOOK_HELP') }}
        </p>
        <div
          v-for="endpoint in webhookEndpoints"
          :key="endpoint.credential"
          class="flex items-center gap-2 mb-2"
        >
          <span
            class="w-16 flex-shrink-0 text-xs font-medium text-n-slate-11 truncate"
          >
            {{ endpoint.credential }}
          </span>
          <code
            class="flex-1 px-3 py-2 text-xs truncate rounded-lg bg-n-alpha-black2"
          >
            {{ endpoint.url }}
          </code>
          <Button
            slate
            xs
            faded
            icon="i-ri-file-copy-line"
            :label="$t('INBOX_MGMT.ADD.ZERNIO.WEBHOOK_COPY')"
            @click="onCopy(endpoint.url)"
          />
        </div>
        <p class="mt-3 text-xs text-n-slate-10">
          {{ $t('INBOX_MGMT.ADD.ZERNIO.WEBHOOK_EVENTS') }}
        </p>
      </div>
    </div>
  </div>
</template>
